"use client";
// Imports
import { Button } from "@/components/ui/button";
import { Container, ComponentWrapper, Icon } from "@/components";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cmsImageUrl } from "@/lib/cmsImageUrl";
import { cn } from "@/lib/utils";

const DEFAULT_CHECK_PATTERN = "/patterns/subscribe-check.svg";
const DEFAULT_BAND_BACKGROUND = "#80BED0"; // dairy blue
const DEFAULT_BUTTON_BACKGROUND = "#151515"; // espresso
const DEFAULT_BUTTON_FOREGROUND = "#E7E4D9"; // wool

function patternUrl(checkPattern) {
  const asset = Array.isArray(checkPattern) ? checkPattern[0] : checkPattern;
  const permalink = asset?.permalink;
  if (!permalink) return DEFAULT_CHECK_PATTERN;

  // Same-origin proxy in dev so CSS background-image can load the CMS asset
  const proxied = cmsImageUrl(permalink, asset?.last_modified, {
    width: asset?.width,
    height: asset?.height,
  });
  return proxied || DEFAULT_CHECK_PATTERN;
}

function resolveButtonColors(buttonBackgroundColor, buttonForegroundColor) {
  return {
    background: buttonBackgroundColor || DEFAULT_BUTTON_BACKGROUND,
    foreground: buttonForegroundColor || DEFAULT_BUTTON_FOREGROUND,
  };
}

// Subscribe — dairy-blue band with name + email signup and check border
export function Subscribe({
  type,
  title,
  description,
  backgroundColor,
  buttonBackgroundColor,
  buttonForegroundColor,
  checkPattern,
}) {
  const bandBackground = backgroundColor || DEFAULT_BAND_BACKGROUND;
  const buttonColors = resolveButtonColors(
    buttonBackgroundColor,
    buttonForegroundColor
  );

  return (
    <ComponentWrapper type={type} className="relative">
      <div
        className="text-brand-espresso"
        style={{ backgroundColor: bandBackground }}
      >
        <Container>
          <div className="py-10 lg:py-14 flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:items-end lg:gap-10 xl:gap-16">
            <div className="text-left lg:pr-4">
              {title && (
                <h2 className="font-heading font-heading-sentence text-brand-espresso text-xl lg:text-[2.5rem] leading-none">
                  {title}
                </h2>
              )}
              {description && (
                <p className="font-body text-brand-espresso text-md lg:text-lg leading-snug mt-3 lg:mt-4 text-pretty">
                  {description}
                </p>
              )}
            </div>
            <div className="w-full">
              <SubscribeForm
                buttonColors={buttonColors}
                bandBackgroundColor={bandBackground}
              />
            </div>
          </div>
        </Container>
        <div
          aria-hidden
          className="w-full h-[21px] sm:h-[28px] lg:h-[42px] bg-repeat-x bg-left bg-top"
          style={{
            backgroundImage: `url("${patternUrl(checkPattern)}")`,
            backgroundSize: "auto 100%",
          }}
        />
      </div>
    </ComponentWrapper>
  );
}

export function SubscribeForm({ buttonColors, bandBackgroundColor } = {}) {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();

    setSubmitting(true);
    setLoading(true);
    setError(null);

    const formData = new FormData(e.target);

    fetch("/api/mailerlite", {
      method: "POST",
      body: JSON.stringify(Object.fromEntries(formData)),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(() => {
        setTimeout(() => {
          setLoading(false);
          setSuccess(true);
        }, 1000);
      })
      .catch((err) => {
        setLoading(false);
        setSubmitting(false);
        setError(err.message);
      });
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="relative w-full">
        <div className="flex flex-col gap-3">
          <input
            placeholder="Your name"
            name="name"
            required
            autoComplete="name"
            className="w-full h-11 px-4 bg-white text-brand-espresso placeholder:text-brand-espresso/40 rounded-l-button-radius rounded-r-button-radius focus:ring-2 focus:ring-brand-orange focus:outline-none"
          />
          <div className="flex w-full">
            <input
              type="email"
              placeholder="your@email.com"
              required
              name="email"
              autoComplete="email"
              className={cn(
                "flex-1 min-w-0 h-11 px-4 bg-white text-brand-espresso",
                "placeholder:text-brand-espresso/40",
                "rounded-l-button-radius rounded-r-none",
                "focus:ring-2 focus:ring-brand-orange focus:outline-none focus:z-10"
              )}
            />
            <Button
              type="submit"
              size="default"
              variant="default"
              customColors={buttonColors}
              className="!rounded-l-none !uppercase shrink-0 px-6 sm:px-8"
              disabled={loading}
            >
              Subscribe
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {submitting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 rounded-button-radius transition-all overflow-hidden"
            >
              <div
                aria-hidden
                className={cn(
                  "absolute inset-0 backdrop-blur-sm",
                  success
                    ? "bg-brand-wool"
                    : !bandBackgroundColor && "bg-header/40"
                )}
                style={
                  !success && bandBackgroundColor
                    ? { backgroundColor: bandBackgroundColor, opacity: 0.4 }
                    : undefined
                }
              />
              <div className="relative flex justify-center items-center p-6 h-full text-brand-espresso">
                {loading && (
                  <div className="animate-spin-slow">
                    <Icon icon="ras-icon" />
                  </div>
                )}
                {success && (
                  <div className="font-body text-md lg:text-lg">
                    Thanks, you&apos;ve been subscribed!
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
      {error && (
        <p className="mt-3 font-body text-sm text-brand-red-gum">
          Error: {error}
        </p>
      )}
    </>
  );
}
